import React, { useState } from 'react';
import { observer } from 'mobx-react';
import { reverse } from 'named-urls';
import { useNavigate } from 'react-router-dom';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Popover, Divider, Tooltip } from '@mui/material';

import routes from 'core/routes';
import { UserRole } from 'core/constants';
import { useStore } from 'utils/hooks/store';

import { ADMIN_AND_MANAGERS } from 'utils/roles/roleGroups';

import ConfirmationModal from 'components/Grid/components/ActionCell/ConfirmationModal';
import { DocumentFileIcon, DocumnetFilledFileIcon } from 'components/Icons/DocumentFileIcon';

import MoveToFolderModal from 'containers/Documents/components/MoveToFolderModal/MoveToFolderModal';

import { FolderTreeItemKind } from 'stores/Folders/types';

import { useFolderDrag, useIsBlockedDropTarget } from './DragContext';
import { ContentWrapper, DragHandle, FolderItem, DocumentName, ExpandIcon, MenuButton, MenuItem } from './styles';

interface DocumentTreeItemProps {
  document: { uuid: string; name: string };
  depth: number;
  currentFolderId: string;
}

const DocumentTreeItem: React.FC<DocumentTreeItemProps> = ({ document, depth, currentFolderId }) => {
  const { folders, documentsDetails, session } = useStore();
  const navigate = useNavigate();
  const { canReorder } = useFolderDrag();
  const isDraggable = canReorder && folders.reorderableUuids.has(document.uuid);
  const isBlockedTarget = useIsBlockedDropTarget(document.uuid, FolderTreeItemKind.Document, currentFolderId);

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: document.uuid,
    disabled: !isDraggable,
  });

  // Only managers/admins can delete documents — a standard User cannot delete
  // a document even when they own it (ENG-11414).
  const canDeleteDocument = ADMIN_AND_MANAGERS.includes(session?.user?.role as UserRole);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [moving, setMoving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const isSelected = folders.selectedDocumentId === document.uuid;

  const handleClick = (): void => {
    navigate(reverse(routes.dashboard.documents.details, { entityId: document.uuid }));
  };

  const handleMenuClick = (e: React.MouseEvent<HTMLElement>): void => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };

  const handleMenuClose = (): void => {
    setAnchorEl(null);
  };

  const handleMoveToFolder = (): void => {
    handleMenuClose();
    setShowMoveModal(true);
  };

  const handleMoveSubmit = async (folderId: string): Promise<void> => {
    setMoving(true);
    const success = await documentsDetails.moveToFolder(document.uuid, folderId);
    setMoving(false);
    if (success) {
      setShowMoveModal(false);
      await folders.loadFolders();
    }
  };

  const handleDeleteDocument = (): void => {
    handleMenuClose();
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async (): Promise<void> => {
    setDeleting(true);
    const success = await documentsDetails.delete(document.uuid);
    setDeleting(false);
    setShowDeleteModal(false);
    if (success) {
      await folders.loadFolders();
      navigate(routes.dashboard.documents.toString());
    }
  };

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <>
      <div ref={setNodeRef} style={style} data-row-uuid={document.uuid}>
        <FolderItem
          data-testid={`document-tree-item-${document.uuid}`}
          $depth={depth}
          $selected={isSelected}
          $blockedTarget={isBlockedTarget}
          onClick={handleClick}
        >
          <DragHandle
            className={isDraggable ? 'drag-handle' : undefined}
            data-testid={isDraggable ? `folder-tree-drag-handle-${document.uuid}` : undefined}
            $enabled={isDraggable}
            onClick={(e) => e.stopPropagation()}
            {...(isDraggable ? { ...attributes, ...listeners, 'aria-label': `Reorder ${document.name}` } : {})}
          >
            {isDraggable && <DragIndicatorIcon fontSize="small" />}
          </DragHandle>
          <ExpandIcon $visible $expanded>
            {isSelected ? <DocumnetFilledFileIcon /> : <DocumentFileIcon />}
          </ExpandIcon>
          <ContentWrapper>
            <Tooltip title={isDragging ? '' : document.name} enterDelay={500}>
              <DocumentName $selected={isSelected}>{document.name}</DocumentName>
            </Tooltip>
          </ContentWrapper>
          <MenuButton
            data-testid={`document-tree-menu-${document.uuid}`}
            className="menu-button"
            size="small"
            onClick={handleMenuClick}
          >
            <MoreVertIcon fontSize="small" />
          </MenuButton>
        </FolderItem>
      </div>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem
          onClick={(e) => {
            e.stopPropagation();
            handleMoveToFolder();
          }}
        >
          Move to Folder
        </MenuItem>
        {canDeleteDocument && (
          <>
            <Divider sx={{ m: 0 }} />
            <MenuItem
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteDocument();
              }}
              sx={{ color: '#D32F2F' }}
            >
              Delete
            </MenuItem>
          </>
        )}
      </Popover>

      {showMoveModal && (
        <MoveToFolderModal
          open={showMoveModal}
          documentName={document.name}
          currentFolderId={currentFolderId}
          loading={moving}
          onClose={() => setShowMoveModal(false)}
          onSubmit={handleMoveSubmit}
        />
      )}

      {showDeleteModal && (
        <ConfirmationModal
          open={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={handleConfirmDelete}
          type="text-input"
          title="Delete document"
          message={`Are you sure you want to delete "${document.name}"?`}
          confirmButtonLabel="Delete"
          loading={deleting}
        />
      )}
    </>
  );
};

export default observer(DocumentTreeItem);
